import type { CanvasPresenceScope } from '@/lib/collaboration/canvas-presence-scope';
import {
  matchesActiveOperatorsPresenceScope,
  matchesResourceElementRemoteSelection,
  matchesStoryboardImageTrackRemoteSelection,
  matchesStoryboardItemRemoteSelection,
  matchesStoryboardResourceTrackRemoteSelection,
  matchesStoryboardTrackRemoteSelection,
  matchesVideoBoardElementRemoteSelection,
  matchesVideoTrackPlaceholderRemoteSelection,
  type CollaborationSelectionTarget,
} from '@/lib/collaboration-presence-ui';
import { create } from 'zustand';

export const COLLABORATION_PRESENCE_TTL_MS = 300_000;

export type CollaborationPresenceContext = 'project_page' | 'parse_flow' | 'canvas';
export type { CanvasPresenceScope as CollaborationPresenceCanvasScope };

export type CollaborationPresenceTargetType =
  | 'storyboard'
  | 'resource_element'
  | 'resource_tag'
  | 'project_resource'
  | 'storyboard_item';

export interface CollaborationPresenceTarget {
  type: CollaborationPresenceTargetType;
  id: string;
  storyboardId?: string | null;
  resourceType?: string | null;
}

export interface CollaborationPresenceUser {
  userId: string;
  displayName?: string | null;
  avatarUrl?: string | null;
  orgKind?: 'host' | 'contractor' | null;
  projectId: string;
  pageContext: CollaborationPresenceContext;
  canvasScope?: CanvasPresenceScope | null;
  selectedTarget?: CollaborationPresenceTarget | null;
  updatedAt: string;
}

export type StoryboardBoardTrackPresenceKind = 'image' | 'resource' | 'combined';

interface CollaborationPresenceState {
  byProjectId: Record<string, Record<string, CollaborationPresenceUser>>;
  activeProjectId: string | null;
  activePageContext: CollaborationPresenceContext | null;
  setActiveScope: (
    projectId: string | null,
    pageContext: CollaborationPresenceContext | null
  ) => void;
  replaceProjectUsers: (projectId: string, users: CollaborationPresenceUser[]) => void;
  upsertUser: (user: CollaborationPresenceUser) => void;
  removeUser: (projectId: string, userId: string) => void;
  clearProject: (projectId: string) => void;
  clearExpired: (now?: number) => void;
  getOnlineUsers: (
    projectId: string | null | undefined,
    pageContext?: CollaborationPresenceContext | null
  ) => CollaborationPresenceUser[];
  getRemoteUsersForTarget: (params: {
    projectId: string | null | undefined;
    targetType: CollaborationPresenceTargetType;
    targetId: string | null | undefined;
    currentUserId?: string | null;
  }) => CollaborationPresenceUser[];
  getRemoteUsersForStoryboardTrackBlock: (params: {
    projectId: string | null | undefined;
    storyboardId: string | null | undefined;
    trackKind?: StoryboardBoardTrackPresenceKind;
    currentUserId?: string | null;
  }) => CollaborationPresenceUser[];
  getRemoteUsersForResourceElement: (params: {
    projectId: string | null | undefined;
    resourceId: string | null | undefined;
    relatedTagIds?: readonly string[];
    currentUserId?: string | null;
  }) => CollaborationPresenceUser[];
  getRemoteUsersForStoryboardItem: (params: {
    projectId: string | null | undefined;
    itemId: string | null | undefined;
    storyboardId?: string | null;
    resourceId?: string | null;
    tagId?: string | null;
    currentUserId?: string | null;
  }) => CollaborationPresenceUser[];
  getRemoteUsersForVideoBoardElement: (params: {
    projectId: string | null | undefined;
    videoId: string | null | undefined;
    boardElementId: string | null | undefined;
    storyboardId?: string | null;
    currentUserId?: string | null;
  }) => CollaborationPresenceUser[];
  getRemoteUsersForVideoTrackPlaceholder: (params: {
    projectId: string | null | undefined;
    boardElementId: string | null | undefined;
    storyboardId?: string | null;
    currentUserId?: string | null;
  }) => CollaborationPresenceUser[];
  getRemoteUsersWithSelection: (params: {
    projectId: string | null | undefined;
    currentUserId?: string | null;
  }) => CollaborationPresenceUser[];
  getMatchedUsersForActiveOperatorsScope: (params: {
    projectId: string | null | undefined;
    scopeTarget: CollaborationSelectionTarget | null | undefined;
  }) => CollaborationPresenceUser[];
}

function getPresenceTime(user: CollaborationPresenceUser): number {
  const timestamp = Date.parse(user.updatedAt);
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function isFresh(user: CollaborationPresenceUser, now: number): boolean {
  return now - getPresenceTime(user) <= COLLABORATION_PRESENCE_TTL_MS;
}

function sortUsers(users: CollaborationPresenceUser[]): CollaborationPresenceUser[] {
  return [...users].sort((a, b) => {
    const byName = (a.displayName || a.userId).localeCompare(b.displayName || b.userId);
    return byName || a.userId.localeCompare(b.userId);
  });
}

export const useCollaborationPresenceStore = create<CollaborationPresenceState>((set, get) => ({
  byProjectId: {},
  activeProjectId: null,
  activePageContext: null,
  setActiveScope: (activeProjectId, activePageContext) =>
    set({ activeProjectId, activePageContext }),
  replaceProjectUsers: (projectId, users) =>
    set((state) => ({
      byProjectId: {
        ...state.byProjectId,
        [projectId]: Object.fromEntries(users.map((user) => [user.userId, user])),
      },
    })),
  upsertUser: (user) =>
    set((state) => ({
      byProjectId: {
        ...state.byProjectId,
        [user.projectId]: {
          ...(state.byProjectId[user.projectId] ?? {}),
          [user.userId]: user,
        },
      },
    })),
  removeUser: (projectId, userId) =>
    set((state) => {
      const current = state.byProjectId[projectId];
      if (!current) return state;
      const remaining = Object.fromEntries(Object.entries(current).filter(([id]) => id !== userId));
      return {
        byProjectId: {
          ...state.byProjectId,
          [projectId]: remaining,
        },
      };
    }),
  clearProject: (projectId) =>
    set((state) => ({
      byProjectId: Object.fromEntries(
        Object.entries(state.byProjectId).filter(([id]) => id !== projectId)
      ),
    })),
  clearExpired: (now = Date.now()) =>
    set((state) => {
      const next: Record<string, Record<string, CollaborationPresenceUser>> = {};
      for (const [projectId, usersById] of Object.entries(state.byProjectId)) {
        const freshEntries = Object.entries(usersById).filter(([, user]) => isFresh(user, now));
        if (freshEntries.length > 0) {
          next[projectId] = Object.fromEntries(freshEntries);
        }
      }
      return { byProjectId: next };
    }),
  getOnlineUsers: (projectId, pageContext) => {
    if (!projectId) return [];
    const now = Date.now();
    return sortUsers(
      Object.values(get().byProjectId[projectId] ?? {}).filter(
        (user) => isFresh(user, now) && (!pageContext || user.pageContext === pageContext)
      )
    );
  },
  getRemoteUsersForTarget: ({ projectId, targetType, targetId, currentUserId }) => {
    if (!projectId || !targetId) return [];
    const now = Date.now();
    return sortUsers(
      Object.values(get().byProjectId[projectId] ?? {}).filter(
        (user) =>
          user.userId !== currentUserId &&
          isFresh(user, now) &&
          user.selectedTarget?.type === targetType &&
          user.selectedTarget.id === targetId
      )
    );
  },
  getRemoteUsersForStoryboardTrackBlock: ({
    projectId,
    storyboardId,
    trackKind = 'combined',
    currentUserId,
  }) => {
    const normalizedStoryboardId = String(storyboardId ?? '').trim();
    if (!projectId || !normalizedStoryboardId) return [];
    const now = Date.now();
    const matcher =
      trackKind === 'image'
        ? matchesStoryboardImageTrackRemoteSelection
        : trackKind === 'resource'
          ? matchesStoryboardResourceTrackRemoteSelection
          : matchesStoryboardTrackRemoteSelection;
    return sortUsers(
      Object.values(get().byProjectId[projectId] ?? {}).filter((user) => {
        if (user.userId === currentUserId || !isFresh(user, now) || !user.selectedTarget) {
          return false;
        }
        return matcher(user.selectedTarget, normalizedStoryboardId);
      })
    );
  },
  getRemoteUsersForResourceElement: ({
    projectId,
    resourceId,
    relatedTagIds = [],
    currentUserId,
  }) => {
    const normalizedResourceId = String(resourceId ?? '').trim();
    if (!projectId || !normalizedResourceId) return [];
    const now = Date.now();
    return sortUsers(
      Object.values(get().byProjectId[projectId] ?? {}).filter((user) => {
        if (user.userId === currentUserId || !isFresh(user, now) || !user.selectedTarget) {
          return false;
        }
        return matchesResourceElementRemoteSelection(
          user.selectedTarget,
          normalizedResourceId,
          relatedTagIds
        );
      })
    );
  },
  getRemoteUsersForStoryboardItem: ({
    projectId,
    itemId,
    storyboardId,
    resourceId,
    tagId,
    currentUserId,
  }) => {
    const normalizedItemId = String(itemId ?? '').trim();
    if (!projectId || !normalizedItemId) return [];
    const now = Date.now();
    return sortUsers(
      Object.values(get().byProjectId[projectId] ?? {}).filter((user) => {
        if (user.userId === currentUserId || !isFresh(user, now) || !user.selectedTarget) {
          return false;
        }
        return matchesStoryboardItemRemoteSelection(user.selectedTarget, {
          itemId: normalizedItemId,
          storyboardId,
          resourceId,
          tagId,
        });
      })
    );
  },
  getRemoteUsersForVideoBoardElement: ({
    projectId,
    videoId,
    boardElementId,
    storyboardId,
    currentUserId,
  }) => {
    const normalizedVideoId = String(videoId ?? '').trim();
    const normalizedBoardElementId = String(boardElementId ?? '').trim();
    if (!projectId || !normalizedVideoId || !normalizedBoardElementId) return [];
    const now = Date.now();
    return sortUsers(
      Object.values(get().byProjectId[projectId] ?? {}).filter((user) => {
        if (user.userId === currentUserId || !isFresh(user, now) || !user.selectedTarget) {
          return false;
        }
        return matchesVideoBoardElementRemoteSelection(user.selectedTarget, {
          videoId: normalizedVideoId,
          boardElementId: normalizedBoardElementId,
          storyboardId,
        });
      })
    );
  },
  getRemoteUsersForVideoTrackPlaceholder: ({
    projectId,
    boardElementId,
    storyboardId,
    currentUserId,
  }) => {
    const normalizedBoardElementId = String(boardElementId ?? '').trim();
    if (!projectId || !normalizedBoardElementId) return [];
    const now = Date.now();
    return sortUsers(
      Object.values(get().byProjectId[projectId] ?? {}).filter((user) => {
        if (user.userId === currentUserId || !isFresh(user, now) || !user.selectedTarget) {
          return false;
        }
        return matchesVideoTrackPlaceholderRemoteSelection(user.selectedTarget, {
          boardElementId: normalizedBoardElementId,
          storyboardId,
        });
      })
    );
  },
  getRemoteUsersWithSelection: ({ projectId, currentUserId }) => {
    if (!projectId) return [];
    const now = Date.now();
    return sortUsers(
      Object.values(get().byProjectId[projectId] ?? {}).filter(
        (user) =>
          user.userId !== currentUserId && isFresh(user, now) && Boolean(user.selectedTarget)
      )
    );
  },
  getMatchedUsersForActiveOperatorsScope: ({ projectId, scopeTarget }) => {
    if (!projectId || !scopeTarget?.id) return [];
    const now = Date.now();
    return sortUsers(
      Object.values(get().byProjectId[projectId] ?? {}).filter(
        (user) =>
          isFresh(user, now) &&
          user.selectedTarget &&
          matchesActiveOperatorsPresenceScope(user.selectedTarget, scopeTarget)
      )
    );
  },
}));
